import { useMemo, useState } from "react";
import { Check, MessageSquareText, Star, X } from "lucide-react";
import { updateReviewStatus } from "../../api/reviews";
import { clubErrorMessage } from "../../api/club";
import { formatDate, formatPhone, toFa } from "./clubFormat";

const FILTERS = [
  { value: "pending", label: "در انتظار بررسی" },
  { value: "approved", label: "تأیید شده" },
  { value: "rejected", label: "رد شده" },
  { value: "all", label: "همه" },
];

const STATUS_STYLES = {
  pending: { label: "در انتظار", className: "text-amber-700" },
  approved: { label: "تأیید شده", className: "text-secondary" },
  rejected: { label: "رد شده", className: "text-gray-500" },
};

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${toFa(value)} از ۵ ستاره`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={13} className={n <= value ? "fill-amber-400 text-amber-400" : "text-gray-200"} aria-hidden="true" />
    ))}
  </span>
);

export default function ReviewsPanel({ reviews, loading, error, onChanged }) {
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState("");

  const counts = useMemo(
    () => reviews.reduce((acc, review) => ({ ...acc, [review.status]: (acc[review.status] || 0) + 1 }), {}),
    [reviews]
  );
  const visible = filter === "all" ? reviews : reviews.filter((review) => review.status === filter);
  const average = reviews.length ? reviews.reduce((sum, r) => sum + (Number(r.stars) || 0), 0) / reviews.length : 0;

  const setStatus = async (review, status) => {
    setBusyId(review._id);
    setActionError("");
    try {
      await updateReviewStatus(review._id, status);
      onChanged({ ...review, status });
    } catch (statusError) {
      setActionError(clubErrorMessage(statusError, "تغییر وضعیت نظر ناموفق بود."));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="rounded-2xl border border-gray-200 bg-white" aria-label="نظرات مشتریان">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="فیلتر نظرات">
          {FILTERS.map((item) => (
            <button
              key={item.value}
              type="button"
              role="tab"
              aria-selected={filter === item.value}
              onClick={() => setFilter(item.value)}
              className={`min-h-8 rounded-lg px-2.5 text-xs font-bold transition-colors ${
                filter === item.value ? "bg-gray-100 text-accent" : "text-gray-500 hover:text-accent"
              }`}
            >
              {item.label}
              {item.value !== "all" && counts[item.value] ? ` (${toFa(counts[item.value])})` : ""}
            </button>
          ))}
        </div>
        {reviews.length > 0 && (
          <p className="flex items-center gap-1.5 text-xs text-gray-500">
            <Star size={14} className="fill-amber-400 text-amber-400" aria-hidden="true" />
            میانگین <strong className="text-accent">{toFa(average.toFixed(1))}</strong> از {toFa(reviews.length)} نظر
          </p>
        )}
      </div>

      {actionError && <p className="mx-4 mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700" role="alert">{actionError}</p>}

      {loading ? (
        <div className="space-y-2 p-4" aria-busy="true">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100" />)}
        </div>
      ) : error ? (
        <p className="p-6 text-center text-sm text-red-700">{error}</p>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center gap-2 p-10 text-center">
          <MessageSquareText size={22} className="text-gray-300" aria-hidden="true" />
          <p className="text-sm font-semibold text-gray-600">
            {filter === "pending" ? "نظری در انتظار بررسی نیست." : "نظری در این بخش نیست."}
          </p>
          <p className="max-w-sm text-xs leading-5 text-gray-400">
            مهمان‌ها از منوی آنلاین نظر ثبت می‌کنند. نظرهایی که تأیید کنید در منو نمایش داده می‌شوند و اگر شماره داشته باشند، به باشگاه اضافه می‌شوند.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {visible.map((review) => {
            const status = STATUS_STYLES[review.status] || STATUS_STYLES.pending;
            return (
              <li key={review._id} className="flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-start">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                    <Stars value={review.stars} />
                    <span className="font-bold text-gray-800">{review.name || "بدون نام"}</span>
                    {review.phone && <span className="text-gray-400" dir="ltr">{formatPhone(review.phone)}</span>}
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-400">{formatDate(review.createdAt)}</span>
                    {filter === "all" && <span className={`font-bold ${status.className}`}>{status.label}</span>}
                  </div>
                  <p className="mt-1.5 text-sm leading-6 text-gray-700">{review.comment}</p>
                </div>
                {review.status === "pending" && (
                  <div className="flex shrink-0 gap-1.5">
                    <button
                      type="button"
                      disabled={busyId === review._id}
                      onClick={() => setStatus(review, "approved")}
                      className="inline-flex min-h-8 items-center gap-1 rounded-lg border border-secondary/20 px-2.5 text-xs font-bold text-secondary hover:bg-secondary_1 disabled:opacity-50"
                    >
                      <Check size={14} aria-hidden="true" /> تأیید
                    </button>
                    <button
                      type="button"
                      disabled={busyId === review._id}
                      onClick={() => setStatus(review, "rejected")}
                      className="inline-flex min-h-8 items-center gap-1 rounded-lg border border-red-100 px-2.5 text-xs font-bold text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      <X size={14} aria-hidden="true" /> رد
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
